import { BooleanRadioButton, BooleanRadioButtonGroup, Callout } from "@octopusdeploy/design-system-components";
import { type DeprecatedFeatureState, type Repository, type DeprecationsConfigurationResource, Permission } from "@octopusdeploy/octopus-server-client";
import { PageContent } from "~/components/PageContent/PageContent";
import { PermissionCheck } from "~/components/PermissionCheck";
import Section from "~/components/Section";
import { ExpandableFormSection, FormSectionHeading, Note, Summary, type SummaryNode } from "~/components/form";
import { Form, useForm } from "~/components/form/Form/Form";
interface DeprecatedFeatureConfigurationSectionProps {
    featureKey: keyof DeprecationsConfigurationResource;
    title: string;
    helpText: string;
    accessibleName: string;
    model: DeprecationsConfigurationResource;
    setModel: (model: DeprecationsConfigurationResource) => void;
}
export interface DeprecationsPageProps {
    loaderData: DeprecationsPageLoaderData;
}
export interface DeprecationsPageLoaderData {
    deprecationConfiguration: DeprecationsConfigurationResource;
}
export async function deprecationsPageLoader(repository: Repository): Promise<DeprecationsPageLoaderData> {
    return {
        deprecationConfiguration: await repository.DeprecationsConfiguration.get(),
    };
}
export const PageTitle = "Deprecated Features";
function getExpiryInformation(lastEnabledAt: string | null) {
    if (!lastEnabledAt)
        return null;
    const lastEnabled = new Date(lastEnabledAt);
    const autoDisableDate = new Date(lastEnabled.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days later
    const now = new Date();
    const timeRemaining = autoDisableDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(timeRemaining / (24 * 60 * 60 * 1000));
    return {
        friendlyDate: autoDisableDate.toLocaleDateString(),
        daysRemaining,
    };
}
function getSummaryText(feature: {
    Enabled: boolean;
    LastEnabledAt: string | null;
}): SummaryNode {
    if (!feature.Enabled) {
        return Summary.summary("Disabled");
    }
    const expiryInfo = getExpiryInformation(feature.LastEnabledAt);
    return Summary.summary(`Enabled${expiryInfo ? ` (will be automatically disabled in ${expiryInfo.daysRemaining} day${expiryInfo.daysRemaining !== 1 ? "s" : ""})` : ""}`);
}
export function DeprecationsPage({ loaderData }: DeprecationsPageProps) {
    const { model, setModel, formProps, createSaveAction } = useForm({
        formName: "Edit Deprecations Configuration",
        initialModel: loaderData.deprecationConfiguration,
        onSubmit: (repository, model) => repository.DeprecationsConfiguration.save({
            DeploymentCountByWeekEndpointLegacyMode: model.DeploymentCountByWeekEndpointLegacyMode.Enabled,
            UserOnboardingLegacyMode: model.UserOnboardingLegacyMode.Enabled,
            CreateBranchLegacyMode: model.CreateBranchLegacyMode.Enabled,
            AzureCloudServiceLegacyMode: model.AzureCloudServiceLegacyMode.Enabled,
            GetAllRunbooksByProjectV1EndpointLegacyMode: model.GetAllRunbooksByProjectV1EndpointLegacyMode.Enabled,
            ScriptCSLegacyMode: model.ScriptCSLegacyMode.Enabled,
        }),
        updateFormModelAfterSubmit: (model) => model,
        submitPermission: { permission: Permission.AdministerSystem },
        snackbarSaveText: "Deprecations configuration updated",
    });
    return (<PageContent header={{ title: PageTitle, primaryAction: createSaveAction() }}>
            <PermissionCheck permission={[Permission.AdministerSystem]}>
                <Section>
                    <p>This page allows you to temporarily restore deprecated features that are being phased out. These features are turned off by default to encourage migration to newer alternatives.</p>
                </Section>
                <Callout type={"warning"} title="Automatic reset">
                    Restored features will automatically be turned off again after 7 days.
                </Callout>
                <Form {...formProps}>
                    <FormSectionHeading title="API Endpoints"/>
                    <DeprecatedFeatureConfigurationSection featureKey="DeploymentCountByWeekEndpointLegacyMode" title="Deployment Count by Week" helpText="'api/reporting/deployments-counted-by-week' has been deprecated and there will be no replacement." accessibleName="Toggle Deployment Count by Week Endpoint" model={model} setModel={setModel}/>
                    <DeprecatedFeatureConfigurationSection featureKey="GetAllRunbooksByProjectV1EndpointLegacyMode" title="Get All Runbooks by Project (V1)" helpText="This endpoint (projects/{projectId}/runbooks/all) has been replaced by 'projects/{projectId}/runbooks/all/v2'." accessibleName="Toggle Get All Runbooks by Project V1 Endpoint" model={model} setModel={setModel}/>
                    <DeprecatedFeatureConfigurationSection featureKey="CreateBranchLegacyMode" title="Create Branch" helpText="This endpoint (projects/{projectId}/git/branches) has been replaced by 'projects/{projectId}/git/branches/v2'." accessibleName="Toggle Create Branch" model={model} setModel={setModel}/>
                    <FormSectionHeading title="Other Features"/>
                    <DeprecatedFeatureConfigurationSection featureKey="AzureCloudServiceLegacyMode" title="Azure Cloud Service" helpText="This feature is deprecated and will be removed in a future release." accessibleName="Toggle Azure Cloud Service" model={model} setModel={setModel}/>
                    <DeprecatedFeatureConfigurationSection featureKey="ScriptCSLegacyMode" title="Script CS Migration" helpText="This feature is deprecated and will be removed in a future release." accessibleName="Toggle Script CS Migration" model={model} setModel={setModel}/>
                </Form>
            </PermissionCheck>
        </PageContent>);
}
function DeprecatedFeatureConfigurationSection({ featureKey, title, helpText, accessibleName, model, setModel }: DeprecatedFeatureConfigurationSectionProps) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const feature = model[featureKey] as DeprecatedFeatureState;
    const expiryInfo = getExpiryInformation(feature.LastEnabledAt);
    return (<ExpandableFormSection key={featureKey} errorKey={featureKey} title={title} summary={getSummaryText(feature)} help={helpText}>
            <BooleanRadioButtonGroup value={feature.Enabled} onChange={(value) => setModel({ ...model, [featureKey]: { ...feature, Enabled: value } })} accessibleName={accessibleName}>
                <BooleanRadioButton value={true} label="Enabled"/>
                <BooleanRadioButton value={false} label="Disabled" isDefault/>
            </BooleanRadioButtonGroup>
            {expiryInfo && <Note>{`will be automatically disabled on ${expiryInfo.friendlyDate}`}</Note>}
        </ExpandableFormSection>);
}
